import { convexQuery } from "@convex-dev/react-query";
import type { Id } from "@convex/_generated/dataModel";
import { applyPhoneNumberMask } from "@plasma/utils/phone-numbers";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { locationsGetById, locationsUpdate } from "~/api-refs";
import {
  LocationAddressField,
  LocationBusinessHoursField,
  LocationLogoField,
  LocationNameField,
  LocationPhoneNumberField,
  LocationStyleAndTypeField,
} from "~/components/locations/location-form-fields";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from "~/components/ui/dialog";
import { Form } from "~/components/ui/form";
import { Skeleton } from "~/components/ui/skeleton";
import { convexClient } from "~/integrations/convex/provider";
import { toConvexTableId } from "~/lib/convex-table-id";
import { logger } from "~/lib/logger";
import { enumIncludes, parseEnumValue } from "~/lib/parse-enum-value";
import { ProductEvents } from "~/lib/posthog";

// V4.1: Route-based modal for URL context tracking
export const Route = createFileRoute(
  "/_authenticated/locations/$id/profile/edit/$field"
)({
  component: EditLocationFieldModal,
});

interface BusinessHour {
  dayOfTheWeek: string;
  isOpen: boolean;
  openTime: string | null;
  closeTime: string | null;
}

interface Address {
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  state: string;
  zipCode: string;
}

interface LocationSchemaType {
  name: string;
  logoUrl: string | null;
  address: Address;
  phoneNumber: string;
  businessHours: BusinessHour[];
  operationalStatus: "OPEN" | "TEMPORARILY_CLOSED" | "PERMANENTLY_CLOSED";
  closeDateRange: {
    closeStartDate: string | null;
    closeEndDate: string | null;
  };
  style: "QSR" | "FSR";
  type: string;
}

const VALID_FIELDS = [
  "logo",
  "name",
  "address",
  "phoneNumber",
  "businessHours",
  "styleAndType",
] as const;
type ValidField = (typeof VALID_FIELDS)[number];
const isValidFieldParam = enumIncludes(VALID_FIELDS);

const FIELD_LABELS: Record<ValidField, string> = {
  logo: "Logo",
  name: "Name",
  address: "Address",
  phoneNumber: "Phone Number",
  businessHours: "Business Hours",
  styleAndType: "Style & Type",
};

const OPERATIONAL_STATUSES = [
  "OPEN",
  "TEMPORARILY_CLOSED",
  "PERMANENTLY_CLOSED",
] as const;
const isOperationalStatus = enumIncludes(OPERATIONAL_STATUSES);
const RESTAURANT_STYLES = ["QSR", "FSR"] as const;
const isRestaurantStyle = enumIncludes(RESTAURANT_STYLES);

const EMPTY_LOCATION_FORM: LocationSchemaType = {
  name: "",
  logoUrl: null,
  address: {
    addressLine1: "",
    addressLine2: null,
    city: "",
    state: "",
    zipCode: "",
  },
  phoneNumber: "",
  businessHours: [],
  operationalStatus: "OPEN",
  closeDateRange: {
    closeStartDate: null,
    closeEndDate: null,
  },
  style: "QSR",
  type: "",
};

interface LocationQueryDoc {
  name: string;
  logoUrl?: string | null;
  phoneNumber?: string | null;
  type: string;
  style: string;
  operationalStatus: string;
  closeStartDate?: string | null;
  closeEndDate?: string | null;
  address: {
    addressLine1: string;
    addressLine2: string | null;
    city: string;
    state: string;
    zipCode: string;
  } | null;
  businessHours: Array<{
    dayOfTheWeek: string;
    isOpen: boolean;
    openTime?: string | null;
    closeTime?: string | null;
  }>;
}

function locationToFormValues(loc: LocationQueryDoc): LocationSchemaType {
  return {
    name: loc.name,
    logoUrl: loc.logoUrl ?? null,
    address: {
      addressLine1: loc.address?.addressLine1 ?? "",
      addressLine2: loc.address?.addressLine2 ?? null,
      city: loc.address?.city ?? "",
      state: loc.address?.state ?? "",
      zipCode: loc.address?.zipCode ?? "",
    },
    phoneNumber: loc.phoneNumber ? applyPhoneNumberMask(loc.phoneNumber) : "",
    businessHours: loc.businessHours.map((bh) => ({
      dayOfTheWeek: bh.dayOfTheWeek,
      isOpen: bh.isOpen,
      openTime: bh.openTime ?? null,
      closeTime: bh.closeTime ?? null,
    })),
    operationalStatus: parseEnumValue(
      loc.operationalStatus,
      isOperationalStatus,
      "OPEN"
    ),
    closeDateRange: {
      closeStartDate: loc.closeStartDate ?? null,
      closeEndDate: loc.closeEndDate ?? null,
    },
    style: parseEnumValue(loc.style, isRestaurantStyle, "QSR"),
    type: loc.type,
  };
}

function EditLocationFieldModal() {
  const { id: locationId, field } = Route.useParams();
  const navigate = useNavigate();
  const [isUploading, setIsUploading] = useState(false);

  // Validate field parameter
  const validField = isValidFieldParam(field) ? field : null;

  const handleClose = () => {
    void navigate({ to: "/locations/$id/profile", params: { id: locationId } });
  };

  // Fetch location data
  const { data: location, isLoading } = useQuery(
    convexQuery(locationsGetById, {
      id: toConvexTableId("locations", locationId),
    })
  );

  const updateLocationMutation = useMutation({
    mutationFn: (data: { id: Id<"locations">; data: LocationSchemaType }) => {
      return convexClient.mutation(locationsUpdate, data);
    },
  });

  const form = useForm<LocationSchemaType>({
    defaultValues: EMPTY_LOCATION_FORM,
  });

  useEffect(() => {
    if (location) {
      form.reset(locationToFormValues(location));
    }
  }, [location, form]);

  const onSubmit = async (data: LocationSchemaType) => {
    if (!location || updateLocationMutation.isPending) {
      return;
    }

    // Clean the address object to remove Convex metadata
    const cleanedAddress = {
      addressLine1: data.address.addressLine1,
      addressLine2: data.address.addressLine2,
      city: data.address.city,
      state: data.address.state,
      zipCode:
        typeof data.address.zipCode === "string"
          ? data.address.zipCode
          : String(data.address.zipCode),
    };

    // Clean the business hours array to remove Convex metadata
    const cleanedBusinessHours = data.businessHours.map((bh) => ({
      dayOfTheWeek: bh.dayOfTheWeek,
      isOpen: bh.isOpen,
      openTime: bh.openTime,
      closeTime: bh.closeTime,
    }));

    // Ensure closeDateRange has proper values
    const cleanedCloseDateRange = {
      closeStartDate: data.closeDateRange?.closeStartDate ?? null,
      closeEndDate: data.closeDateRange?.closeEndDate ?? null,
    };

    // Only include the exact fields required by the backend mutation
    const cleanedData = {
      name: data.name,
      logoUrl: data.logoUrl ?? null,
      address: cleanedAddress,
      phoneNumber: data.phoneNumber ?? null,
      businessHours: cleanedBusinessHours,
      operationalStatus: data.operationalStatus,
      closeDateRange: cleanedCloseDateRange,
      style: data.style,
      type: data.type,
    };

    try {
      const result = await updateLocationMutation.mutateAsync({
        id: location._id,
        data: cleanedData,
      });
      if (result.success) {
        // Track location update in PostHog.
        if (validField) {
          ProductEvents.locationUpdated(locationId, validField);
        }
        handleClose();
      }
    } catch (error) {
      logger.exception("Error updating location", error);
    }
  };

  const renderFormField = () => {
    if (!validField) {
      return null;
    }

    switch (validField) {
      case "logo":
        return (
          <LocationLogoField
            control={form.control}
            name="logoUrl"
            setUploading={setIsUploading}
          />
        );
      case "name":
        return <LocationNameField control={form.control} name="name" />;
      case "address":
        return (
          <LocationAddressField
            addressLine1Field="address.addressLine1"
            addressLine2Field="address.addressLine2"
            cityField="address.city"
            control={form.control}
            resetField={form.resetField}
            stateField="address.state"
            zipCodeField="address.zipCode"
          />
        );
      case "phoneNumber":
        return (
          <LocationPhoneNumberField control={form.control} name="phoneNumber" />
        );
      case "businessHours":
        return (
          <LocationBusinessHoursField
            closeEndDateField="closeDateRange.closeEndDate"
            closeStartDateField="closeDateRange.closeStartDate"
            control={form.control}
            operationalStatusField="operationalStatus"
            resetField={form.resetField}
            watch={form.watch}
          />
        );
      case "styleAndType":
        return (
          <LocationStyleAndTypeField
            control={form.control}
            resetField={form.resetField}
            styleField="style"
            typeField="type"
            watch={form.watch}
          />
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <Dialog onOpenChange={handleClose} open>
        <DialogContent
          className="max-h-full scrollbar-none overflow-y-scroll p-6"
          noScrollPrevention
        >
          <Skeleton className="h-48 w-full" />
        </DialogContent>
      </Dialog>
    );
  }

  if (!(location && validField)) {
    return (
      <Dialog onOpenChange={handleClose} open>
        <DialogContent
          className="max-h-full scrollbar-none overflow-y-scroll p-6"
          noScrollPrevention
        >
          <div className="flex flex-col items-center justify-center py-8">
            <p className="text-muted-foreground">
              {validField ? "Location not found" : "Invalid field"}
            </p>
            <Button
              className="mt-4"
              onClick={handleClose}
              type="button"
              variant="outline"
            >
              Back to Profile
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const isPending =
    updateLocationMutation.isPending || (validField === "logo" && isUploading);

  return (
    <Dialog onOpenChange={handleClose} open>
      <DialogContent
        className="max-h-full scrollbar-none overflow-y-scroll p-6"
        noScrollPrevention
      >
        <Form {...form}>
          <form className="space-y-5" onSubmit={form.handleSubmit(onSubmit)}>
            <DialogTitle className="text-center">
              Update {FIELD_LABELS[validField].toLowerCase()}
            </DialogTitle>
            {renderFormField()}
            <DialogFooter>
              <Button
                className="w-full"
                onClick={handleClose}
                size="lg"
                type="button"
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                className="w-full"
                disabled={isPending}
                size="lg"
                type="submit"
              >
                {isUploading ? "Uploading image..." : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
